import { apiClient } from '@/api/client';
import type { PartyType, PartyPricingTier, TenantPartyFormValues } from '@/schemas';

/**
 * A row in the tenant's "My Parties" book. Mirrors backend
 * `TenantPartyDto.java`.
 *
 * Two ways a row gets here:
 *   1. Pushed by an external vendor application (bulk endpoint).
 *      `sourceApp` will be set in that case.
 *   2. Created manually from the "My Parties" tab in the parties UI
 *      (`createTenantParty` below). `sourceApp` is null then.
 *
 * `matchedPartyId` is set whenever a wholesaler-side {@link Party}
 * shares the same GSTIN — done automatically on push / create, or
 * manually via `mapPartyToTenant`.
 */
export interface TenantParty {
  id: number;
  sourceApp?: string;
  externalId?: string;
  tenantSyncPartyId?: string;
  outstanding?: number;

  // Identity
  name: string;
  partyCode?: string;
  partyExternalId?: string;
  partyType?: PartyType;
  defaultPricing?: PartyPricingTier;

  // Contact
  contactPerson?: string;
  mobile?: string;
  phone?: string;
  email?: string;

  // Tax / licence
  gstin?: string;
  dlNumber?: string;
  /** ISO yyyy-MM-dd from the backend. */
  dlExpiry?: string;
  panNumber?: string;

  // Sales rep
  repName?: string;

  // Address — both legacy free-form (address / place) and new structured
  // columns (line1, line2, area, pincode). The Create Party form
  // populates the structured fields; bulk push uses the legacy ones.
  address?: string;
  place?: string;
  addressLine1?: string;
  addressLine2?: string;
  area?: string;
  city?: string;
  pincode?: string;

  matchedPartyId?: number;
  pushedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface TenantPartyPage {
  content: TenantParty[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/**
 * GET /tenant-parties — searches across name, GSTIN and party code.
 *
 * The All-Parties autocomplete passes `unmatched=true` so we never offer
 * a tenant party that is already pinned to another party row. The
 * "My Parties" tab passes `unmatched=false` to show every row.
 */
export async function listTenantParties(
  q?: string,
  unmatched = false,
  page = 0,
  size = 25,
  searchField?: import('./tenant').SearchField,
): Promise<TenantPartyPage> {
  const { data } = await apiClient.get<TenantPartyPage>('/tenant-parties', {
    params: { q: q || undefined, unmatched, page, size, searchField: (q && searchField) || undefined },
  });
  return data;
}

/** GET /tenant-parties/{id} */
export async function getTenantParty(id: number): Promise<TenantParty> {
  const { data } = await apiClient.get<TenantParty>(`/tenant-parties/${id}`);
  return data;
}

/**
 * POST /tenant-parties — manual create from the "My Parties" tab.
 *
 * Distinct from the bulk push: there is no sourceApp / externalId on
 * the payload (the row is owned by this tenant directly). GSTIN
 * auto-match still runs server-side.
 */
export async function createTenantParty(
  payload: TenantPartyFormValues,
): Promise<TenantParty> {
  const { data } = await apiClient.post<TenantParty>(
    '/tenant-parties',
    payload,
  );
  return data;
}

/** PUT /tenant-parties/{id} — edit a tenant party (name, gstin, etc). */
export async function updateTenantParty(
  id: number,
  payload: TenantPartyFormValues,
): Promise<TenantParty> {
  const { data } = await apiClient.put<TenantParty>(
    `/tenant-parties/${id}`,
    payload,
  );
  return data;
}

/**
 * POST /tenant-parties/{id}/link-peer?peerId={peerId}
 * Links this TenantParty to a peer tenant in the cross-tenant directory.
 * Any previously linked peer on this TenantParty is replaced; any other
 * TenantParty that was already linked to the same peer is cleared first.
 */
export async function linkPeerToTenantParty(
  tenantPartyId: number,
  peerId: number,
): Promise<TenantParty> {
  const { data } = await apiClient.post<TenantParty>(
    `/tenant-parties/${tenantPartyId}/link-peer`,
    null,
    { params: { peerId } },
  );
  return data;
}

/**
 * DELETE /tenant-parties/{id}/link-peer
 * Removes the peer-tenant link from this TenantParty.
 */
export async function unlinkPeerFromTenantParty(
  tenantPartyId: number,
): Promise<TenantParty> {
  const { data } = await apiClient.delete<TenantParty>(
    `/tenant-parties/${tenantPartyId}/link-peer`,
  );
  return data;
}

/**
 * POST /tenant-parties/block-peer/{peerId} — block a peer tenant.
 * The block is persisted server-side. Blocked peers remain visible in
 * the directory but cannot be linked, and future invoices are rejected.
 */
export async function blockPeer(peerId: number): Promise<void> {
  await apiClient.post(`/tenant-parties/block-peer/${peerId}`);
}

/**
 * DELETE /tenant-parties/block-peer/{peerId} — unblock a peer tenant.
 */
export async function unblockPeer(peerId: number): Promise<void> {
  await apiClient.delete(`/tenant-parties/block-peer/${peerId}`);
}

/**
 * PATCH /tenant-parties/{id}/pricing
 * Updates only the defaultPricing field — mirrors the same sub-resource
 * pattern used for parties (`/parties/{id}/pricing`).
 */
export async function updateTenantPartyPricing(
  id: number,
  defaultPricing: PartyPricingTier,
): Promise<TenantParty> {
  const { data } = await apiClient.patch<TenantParty>(
    `/tenant-parties/${id}/pricing`,
    { defaultPricing },
  );
  return data;
}

/**
 * POST /tenant-parties/bulk — push a batch of parties from an
 * external vendor application. The server upserts by
 * (sourceApp, externalId) when both are provided, otherwise inserts.
 */
export async function pushTenantParties(
  rows: Omit<TenantParty, 'id' | 'matchedPartyId' | 'pushedAt' | 'createdAt' | 'updatedAt'>[],
): Promise<TenantParty[]> {
  const { data } = await apiClient.post<TenantParty[]>(
    '/tenant-parties/bulk',
    rows,
  );
  return data;
}

// ------------------------------------------------------------------
// My Network Invoices
// ------------------------------------------------------------------

export interface MyNetworkInvoice {
  id?: number;
  invoiceNumber: string;
  invoiceDate?: string;
  invoiceAmount?: number;
  paid?: number;
  due?: number;
  createdAt?: string;
  updatedAt?: string;
}

/** One group in the POST /tenant-parties/invoices request body. */
export interface NetworkInvoicesPushGroup {
  tenantSyncPartyId: string;
  invoices: Omit<MyNetworkInvoice, 'id' | 'createdAt' | 'updatedAt'>[];
}

/** Per-group result returned by POST /tenant-parties/invoices. */
export interface NetworkInvoicesPushResult {
  tenantSyncPartyId: string;
  tenantPartyId: number | null;
  status: 'OK' | 'PARTY_NOT_FOUND';
  invoices: MyNetworkInvoice[];
  outstanding: number | null;
}

/**
 * POST /tenant-parties/invoices — bulk push (upsert) invoices for many
 * parties in one call. Each group is matched to a party by
 * `tenantSyncPartyId` (the same ID sent on `pushTenantParties`) instead
 * of the party's internal row id, so callers never need to look that up.
 * Updates each matched party's `outstanding` total automatically.
 */
export async function pushNetworkInvoicesBulk(
  groups: NetworkInvoicesPushGroup[],
): Promise<NetworkInvoicesPushResult[]> {
  const { data } = await apiClient.post<NetworkInvoicesPushResult[]>(
    '/tenant-parties/invoices',
    groups,
  );
  return data;
}

/** Get all invoices for a My Network party. */
export async function getNetworkInvoices(tenantPartyId: number): Promise<MyNetworkInvoice[]> {
  const { data } = await apiClient.get<MyNetworkInvoice[]>(
    `/tenant-parties/${tenantPartyId}/invoices`,
  );
  return data;
}

/** Get outstanding total for a My Network party. */
export async function getNetworkOutstanding(
  tenantPartyId: number,
): Promise<{ tenantPartyId: number; outstanding: number }> {
  const { data } = await apiClient.get(
    `/tenant-parties/${tenantPartyId}/outstanding`,
  );
  return data;
}
